<script setup lang="ts">
import { useApplicationStore } from "./store/application";
import { useUserStore } from "./store/user";

const pageContainerEl = ref<HTMLElement>();

const router = useRouter();
const route = useRoute();
const userStore = useUserStore();
const applicationStore = useApplicationStore();
const isLoading = computed(() => !applicationStore.isInitialized);

const isNavbarDisplayed = ref(false);

const isPublicPage = computed(() => !!route.meta?.isPublic);

const isPublicPageTarget = ref(true);

const isLoadingScreenVisible = computed(
  () => !isPublicPageTarget.value && !isPublicPage.value && isLoading.value,
);

const expiredSocialMediaPages = computed(() => applicationStore.expiredSocialMediaPages);
const isPageExpirationDialogConfirmed = ref(false);
const isPageExpirationDialogVisible = computed(
  () =>
    !isLoading.value &&
    expiredSocialMediaPages.value.length > 0 &&
    !isPageExpirationDialogConfirmed.value,
);

const setPageExpirationDialogConfirmation = (isConfirmed: boolean) => {
  isPageExpirationDialogConfirmed.value = isConfirmed;
};

const navigateToSettings = () => {
  setPageExpirationDialogConfirmation(true);
  router.push(`/admin?platformConnect=${expiredSocialMediaPages.value[0]?.socialMediaType}`);
};

const isElevatedUser = computed(() => userStore.isElevatedUser);

watch(route, (to, from) => {
  const isPublicPage = !!to.meta.isPublic;
  isPublicPageTarget.value = isPublicPage;
  isNavbarDisplayed.value = !isPublicPage;
  // note: scroll behavior on the router was not working due to mandatory overflow-y-scroll on the parent el of <router-view>
  pageContainerEl.value?.scrollTo({ top: 0 });
});
</script>

<template>
  <LoadingScreen v-if="isLoadingScreenVisible" class="absolute inset-0 z-100" />
  <div class="custom-scrollbar relative flex min-h-screen overflow-x-auto bg-white">
    <NavBar v-if="isNavbarDisplayed"></NavBar>
    <main
      id="main-scroll-container"
      class="custom-scrollbar h-screen w-full min-w-[600px]"
      :class="!isPublicPage ? 'overflow-y-scroll' : 'flex overflow-y-auto'"
      aria-labelledby="cmt-page-title"
    >
      <router-view v-if="!isLoadingScreenVisible" />
    </main>
    <ToastNotificationQueue />
    <SocialMediaPageExpiredDialog
      :isOpen="isPageExpirationDialogVisible"
      :isAdministrator="isElevatedUser"
      :expiredPages="expiredSocialMediaPages"
      @confirm="navigateToSettings()"
      @close="setPageExpirationDialogConfirmation(true)"
    />
  </div>
</template>

<style>
body {
  font-family: "TheSansB";
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  overflow: hidden;
}

.custom-header-gradient {
  background: rgb(255, 255, 255);
  background: linear-gradient(
    180deg,
    rgb(255, 255, 255) 0%,
    rgb(255, 255, 255) 90%,
    rgba(255, 255, 255, 1) 100%,
    rgba(255, 255, 255, 0) 100%
  );
}

.custom-scrollbar::-webkit-scrollbar {
  width: 14px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  border: 4px solid rgba(0, 0, 0, 0);
  background-clip: padding-box;
  border-radius: 9999px;
  background-color: #e4e8ed;
}

button {
  user-select: none;
}

/* used for skeletons */

.skeleton-shine-animation {
  position: relative;
  overflow: hidden;
}

.skeleton-shine-animation::after {
  position: absolute;
  inset: 0;
  content: "";

  background: linear-gradient(
    95deg,
    transparent 0%,
    rgba(255, 255, 255, 0.5) 50%,
    transparent 100%
  );
  animation: shine 1s ease-in-out infinite;
}

@keyframes shine {
  from {
    transform: translate3d(-100%, 0, 0);
  }
  to {
    transform: translate3d(100%, 0, 0);
  }
}

.skeleton-pulse-animation {
  animation: pulse 1s ease-in-out infinite;
}

@keyframes pulse {
  from {
    opacity: 1;
  }
  50% {
    opacity: 0.75;
  }
  to {
    opacity: 1;
  }
}

*:focus-visible {
  outline: 2.5px solid #042B5F;
  outline-offset: 4px;
}

mark {
  background-color: #ffb6d3;
  color: #212730;
  font-weight: 600;
}

mark a {
  font-weight: 700;
}

</style>
